import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import PageHeader from "../common/PageHeader";
import Loader from "../common/Loader";
import EmptyState from "../common/EmptyState";
import {
	getCompanyDetails,
	updateCompanyDetails,
	createDwollaBusinessAccount,
	updateDwollaBusinessAccount,
	getBeneficialOwnerList,
	addBeneficialOwner,
	uploadBeneficialDocuments,
	uploadDocuments,
	certifiedDwollaUser,
	getBuyerStatusList,
	getDwollaUserInfo,
} from "../../api/funds";

const TABS = ["Company Details", "Controller", "Beneficial Owners", "Certify"];

export default function CompanyVerification() {
	const [tab, setTab] = useState("Company Details");
	const [status, setStatus] = useState(null);

	useEffect(() => {
		(async () => {
			const res = await getBuyerStatusList();
			if (res && !res.error) setStatus(res.data);
		})();
	}, []);

	return (
		<div>
			<PageHeader
				title="Business Verification"
				subtitle="Dwolla identity verification is required before you can send or receive funds."
			/>
			{status ? (
				<div className="card p-3 mb-3 d-flex flex-row align-items-center justify-content-between">
					<span className="text-muted small">Verification status</span>
					<span className="status-pill">{status.dwollaStatus || status.status || "not started"}</span>
				</div>
			) : null}
			<ul className="nav nav-tabs mb-3">
				{TABS.map((t) => (
					<li className="nav-item" key={t}>
						<button className={`nav-link ${tab === t ? "active" : ""}`} onClick={() => setTab(t)}>
							{t}
						</button>
					</li>
				))}
			</ul>
			{tab === "Company Details" && <CompanyDetailsTab />}
			{tab === "Controller" && <ControllerTab />}
			{tab === "Beneficial Owners" && <BeneficialOwnersTab />}
			{tab === "Certify" && <CertifyTab />}
		</div>
	);
}

function CompanyDetailsTab() {
	const [loading, setLoading] = useState(true);
	const [exists, setExists] = useState(false);
	const [form, setForm] = useState({
		corporateName: "",
		mailingAddress: "",
		city: "",
		state: "",
		zip: "",
		businessType: "",
		businessClassification: "",
		ein: "",
	});
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		(async () => {
			setLoading(true);
			const res = await getCompanyDetails();
			if (res && !res.error && res.data) {
				setExists(true);
				setForm((f) => ({ ...f, ...res.data }));
			}
			setLoading(false);
		})();
	}, []);

	const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

	const submit = async () => {
		setSaving(true);
		const res = await updateCompanyDetails(form);
		setSaving(false);
		if (res && !res.error) {
			toast.success(exists ? "Company details updated" : "Company details saved");
			setExists(true);
		} else {
			toast.error(res?.msg || "Could not save company details");
		}
	};

	if (loading) return <Loader />;

	return (
		<div className="card p-4" style={{ maxWidth: 640 }}>
			<div className="row g-3">
				<Field label="Corporate / legal name" value={form.corporateName} onChange={set("corporateName")} />
				<Field label="EIN" value={form.ein} onChange={set("ein")} />
				<Field label="Mailing address" value={form.mailingAddress} onChange={set("mailingAddress")} full />
				<Field label="City" value={form.city} onChange={set("city")} half />
				<Field label="State" value={form.state} onChange={set("state")} half />
				<Field label="Zip" value={form.zip} onChange={set("zip")} half />
				<Field label="Business type" value={form.businessType} onChange={set("businessType")} half placeholder="corporation / llc / partnership" />
			</div>
			<button className="btn-accent-solid mt-3" style={{ width: 200 }} disabled={saving} onClick={submit}>
				{saving ? "Saving..." : exists ? "Update Company" : "Save Company"}
			</button>
		</div>
	);
}

function ControllerTab() {
	const [form, setForm] = useState({
		firstName: "",
		lastName: "",
		title: "",
		dateOfBirth: "",
		ssn: "",
		ein: "",
		address1: "",
		address2: "",
		city: "",
		state: "",
		zip: "",
		country: "US",
	});
	const [saving, setSaving] = useState(false);
	const [file, setFile] = useState(null);
	const [accountExists, setAccountExists] = useState(false);

	useEffect(() => {
		(async () => {
			const [info, company] = await Promise.all([getDwollaUserInfo(), getCompanyDetails()]);
			if (info && !info.error && info.data && Object.keys(info.data).length) setAccountExists(true);
			if (company && !company.error && company.data?.ein) setForm((f) => ({ ...f, ein: company.data.ein }));
		})();
	}, []);

	const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

	const submit = async () => {
		setSaving(true);
		const { dateOfBirth, zip, ...rest } = form;
		const payload = { ...rest, dob: dateOfBirth, postalCode: zip };
		const res = await (accountExists ? updateDwollaBusinessAccount : createDwollaBusinessAccount)(payload);
		if (res && !res.error) setAccountExists(true);
		if (res && !res.error && file) {
			await uploadDocuments(toFormData(file, "governmentId"));
		}
		setSaving(false);
		if (res && !res.error) {
			toast.success("Controller details saved");
		} else {
			toast.error(res?.msg || "Could not save controller details");
		}
	};

	return (
		<div className="card p-4" style={{ maxWidth: 640 }}>
			<div className="text-muted small mb-3">
				The controller is the person with significant management responsibility (CEO, CFO, President, etc.).
			</div>
			<div className="row g-3">
				<Field label="First name" value={form.firstName} onChange={set("firstName")} half />
				<Field label="Last name" value={form.lastName} onChange={set("lastName")} half />
				<Field label="Job title" value={form.title} onChange={set("title")} half />
				<Field label="Date of birth" value={form.dateOfBirth} onChange={set("dateOfBirth")} half type="date" />
				<Field label="SSN (9 digits)" value={form.ssn} onChange={set("ssn")} half />
				<Field label="Company EIN" value={form.ein} onChange={set("ein")} half />
				<Field label="Address line 1" value={form.address1} onChange={set("address1")} full />
				<Field label="Address line 2" value={form.address2} onChange={set("address2")} full />
				<Field label="City" value={form.city} onChange={set("city")} third />
				<Field label="State" value={form.state} onChange={set("state")} third />
				<Field label="Zip" value={form.zip} onChange={set("zip")} third />
				<div className="col-12">
					<label className="form-label">Government ID (photo/scan)</label>
					<input type="file" className="form-control" onChange={(e) => setFile(e.target.files?.[0] || null)} />
				</div>
			</div>
			<button className="btn-accent-solid mt-3" style={{ width: 200 }} disabled={saving} onClick={submit}>
				{saving ? "Saving..." : "Save Controller"}
			</button>
		</div>
	);
}

function BeneficialOwnersTab() {
	const [loading, setLoading] = useState(true);
	const [owners, setOwners] = useState([]);
	const [showAdd, setShowAdd] = useState(false);

	const load = async () => {
		setLoading(true);
		const res = await getBeneficialOwnerList();
		if (res && !res.error) setOwners(res.data || []);
		setLoading(false);
	};

	useEffect(() => {
		load();
	}, []);

	if (loading) return <Loader />;

	const totalPct = owners.reduce((sum, o) => sum + (Number(o.percentageOwnership || o.percentage) || 0), 0);

	return (
		<div>
			<div className="d-flex justify-content-between align-items-center mb-3">
				<div className="text-muted small">
					Anyone owning 25% or more of the business must be listed. Total declared: <strong>{totalPct}%</strong>
				</div>
				<button className="btn-accent-solid" onClick={() => setShowAdd(true)}>
					+ Add Owner
				</button>
			</div>
			{owners.length === 0 ? (
				<EmptyState title="No beneficial owners added" subtitle="Add anyone who owns 25% or more of the company." />
			) : (
				<div className="card">
					<table className="table align-middle mb-0">
						<thead>
							<tr className="text-muted small">
								<th className="ps-3">Name</th>
								<th>Ownership %</th>
								<th>Status</th>
							</tr>
						</thead>
						<tbody>
							{owners.map((o) => (
								<tr key={o.id || o._id}>
									<td className="ps-3">{`${o.firstName || ""} ${o.lastName || ""}`.trim()}</td>
									<td>{o.percentageOwnership || o.percentage || "-"}%</td>
									<td>
										<span className="status-pill success">{o.verificationStatus || "pending"}</span>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
			{showAdd && <AddOwnerModal onClose={() => setShowAdd(false)} onDone={() => { setShowAdd(false); load(); }} />}
		</div>
	);
}

function AddOwnerModal({ onClose, onDone }) {
	const [form, setForm] = useState({
		firstName: "",
		lastName: "",
		dateOfBirth: "",
		ssn: "",
		percentageOwnership: "",
		address1: "",
		city: "",
		state: "",
		zip: "",
		country: "US",
	});
	const [file, setFile] = useState(null);
	const [submitting, setSubmitting] = useState(false);

	const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

	const submit = async () => {
		if (!form.firstName || !form.lastName || !form.percentageOwnership) {
			toast.error("Fill in name and ownership percentage");
			return;
		}
		setSubmitting(true);
		const { dateOfBirth, zip, ...rest } = form;
		const res = await addBeneficialOwner({ ...rest, dob: dateOfBirth, postalCode: zip });
		if (res && !res.error && file) {
			const formData = toFormData(file, "governmentId");
			formData.append("ownerId", res.data?.id || res.data?._id || "");
			await uploadBeneficialDocuments(formData);
		}
		setSubmitting(false);
		if (res && !res.error) {
			toast.success("Owner added");
			onDone();
		} else {
			toast.error(res?.msg || "Could not add owner");
		}
	};

	return (
		<div className="modal d-block" style={{ background: "rgba(0,0,0,0.4)" }}>
			<div className="modal-dialog modal-dialog-centered modal-lg">
				<div className="modal-content p-4">
					<h6 className="fw-bold mb-3">Add Beneficial Owner</h6>
					<div className="row g-3">
						<Field label="First name" value={form.firstName} onChange={set("firstName")} half />
						<Field label="Last name" value={form.lastName} onChange={set("lastName")} half />
						<Field label="Date of birth" value={form.dateOfBirth} onChange={set("dateOfBirth")} half type="date" />
						<Field label="SSN" value={form.ssn} onChange={set("ssn")} half />
						<Field label="Ownership %" value={form.percentageOwnership} onChange={set("percentageOwnership")} half type="number" />
						<Field label="Address" value={form.address1} onChange={set("address1")} half />
						<Field label="City" value={form.city} onChange={set("city")} third />
						<Field label="State" value={form.state} onChange={set("state")} third />
						<Field label="Zip" value={form.zip} onChange={set("zip")} third />
						<div className="col-12">
							<label className="form-label">Government ID</label>
							<input type="file" className="form-control" onChange={(e) => setFile(e.target.files?.[0] || null)} />
						</div>
					</div>
					<div className="d-flex gap-2 mt-3">
						<button className="btn-accent-solid" disabled={submitting} onClick={submit}>
							{submitting ? "Saving..." : "Add Owner"}
						</button>
						<button className="btn-outline" onClick={onClose}>
							Cancel
						</button>
					</div>
				</div>
			</div>
		</div>
	);
}

function CertifyTab() {
	const [submitting, setSubmitting] = useState(false);
	const [certified, setCertified] = useState(false);

	const submit = async () => {
		setSubmitting(true);
		const res = await certifiedDwollaUser();
		setSubmitting(false);
		if (res && !res.error) {
			setCertified(true);
			toast.success("Business verification submitted");
		} else {
			toast.error(res?.msg || "Could not submit certification");
		}
	};

	return (
		<div className="card p-4" style={{ maxWidth: 560 }}>
			<p className="text-muted">
				By certifying, you confirm that the company, controller and beneficial ownership information provided is accurate
				and complete, as required for identity verification with Dwolla.
			</p>
			<button className="btn-accent-solid" style={{ width: 220 }} disabled={submitting || certified} onClick={submit}>
				{certified ? "Submitted" : submitting ? "Submitting..." : "Certify & Submit"}
			</button>
		</div>
	);
}

function Field({ label, value, onChange, full, half, third, type = "text", placeholder }) {
	const col = full ? "col-12" : third ? "col-md-4" : half ? "col-md-6" : "col-md-6";
	return (
		<div className={col}>
			<label className="form-label">{label}</label>
			<input type={type} className="form-control" value={value || ""} onChange={onChange} placeholder={placeholder} />
		</div>
	);
}

function toFormData(file, docType) {
	const formData = new FormData();
	formData.append("document", file);
	formData.append("documentType", docType);
	return formData;
}
